import {combineReducers} from 'redux';
import {createSlice} from '@reduxjs/toolkit';

import {ISelectionSlice} from '../../../types/redux';
import {
    DnsRecord,
    recordKey,
} from '../types';

const entityKey = 'dnsRecords';

const initialState: ISelectionSlice = {};

// FNP-1293 — SELECTION MOVES INTO REDUX, replacing the module-scoped
// `let globalSelection` + listener `Set` this module used to carry. The old
// store lived outside React, so it survived unmount and route changes and
// leaked between test cases — which is why it needed a
// `resetDnsRecordsSelection` export for the suite to call by hand. A slice
// needs neither: `setupStore` builds a fresh one per render.
//
// ⚠️ NOT BUILT FROM `SelectionSliceFor`, AND THIS IS THE ONE PLACE IN THIS APP
// WHERE THAT HELPER DOES NOT FIT.
//
// `SelectionSliceFor` keys its state on `record.id`. A DNS record HAS NO `id`
// — the schema has no such field, and `types.ts` is emphatic that nothing may
// invent one, because a synthetic id would be a value the API never sent and
// can never accept back. Keying on `record.id` here would write every selected
// record to the single key `undefined`, so ticking a second row would silently
// replace the first.
//
// The record's real identity within a zone is (name, type), which `recordKey`
// already composes for `MantineDataTable`'s `idAccessor`. The slice uses that
// SAME function, so the key the table uses to mark a row selected and the key
// the store files it under cannot drift apart.
//
// The action surface matches `SelectionSliceFor` exactly — addToSelection,
// removeFromSelection, clearSelection — so a reader moving between this module
// and the four that use the helper finds the same vocabulary.
const selectionSlice = createSlice({
                                       name: entityKey,
                                       initialState,
                                       reducers: {
                                           addToSelection: (state, action) => {
                                               const record = action.payload as DnsRecord;
                                               state[recordKey(record)] = record;
                                           },
                                           // ⚠️ Takes the composed KEY, not the record —
                                           // matching SelectionSliceFor, whose
                                           // removeFromSelection takes an id.
                                           removeFromSelection: (state, action) => {
                                               delete state[action.payload as string];
                                           },
                                           clearSelection: (state) => {
                                               Object.keys(state)
                                                     .forEach((key) => {
                                                         delete state[key];
                                                     });
                                           },
                                       },
                                   });

export const {
    addToSelection,
    removeFromSelection,
    clearSelection,
} = selectionSlice.actions;

// ⚠️ NO VISIBILITY SLICE. This module's keyword and filters live in their own
// module-scoped stores (FNP-815) and are out of scope for this ticket.
export const DnsRecordsReducer = combineReducers({
                                                     selection: selectionSlice.reducer,
                                                 });
